// src/services/dataPersistence/syncManager.ts
import { Itinerary } from './types';
import { itineraryApiService } from '../api/itineraryApi';
import { authTokenManager } from '../../utils/authTokenManager';

/**
 * Status of the sync operation
 */
export type SyncStatus = 'idle' | 'syncing' | 'error' | 'success';

/**
 * Sync direction options
 */
export type SyncDirection = 'pull' | 'push' | 'both';

/**
 * Conflict resolution strategy
 */
export type ConflictStrategy = 'local' | 'remote' | 'merge';

/**
 * Options for sync operations
 */
export interface SyncOptions {
  direction?: SyncDirection;
  conflictStrategy?: ConflictStrategy;
  force?: boolean;
}

/**
 * Result of a sync operation
 */
export interface SyncResult {
  success: boolean;
  added: number;
  updated: number;
  deleted: number;
  conflicts: number;
  error?: string;
}

/**
 * Manages synchronization between local storage and remote database
 */
export class SyncManager {
  private static instance: SyncManager;
  
  // Sync state
  private syncStatus: SyncStatus = 'idle';
  private lastSyncTime: number = 0;
  private syncError: string | null = null;
  private syncQueue: Array<() => Promise<void>> = [];
  private isSyncingActive: boolean = false;
  private isOnline: boolean = navigator.onLine;
  
  // Default options
  private defaultOptions: SyncOptions = {
    direction: 'both',
    conflictStrategy: 'merge',
    force: false
  };
  
  // Events
  private listeners: {
    [key: string]: Array<(data?: any) => void>;
  } = {
    statusChange: [],
    syncComplete: [],
    syncError: [],
    connectionChange: []
  };
  
  private constructor() {
    // Listen for online/offline events
    window.addEventListener('online', this.handleOnlineStatusChange.bind(this));
    window.addEventListener('offline', this.handleOnlineStatusChange.bind(this));
  }
  
  /**
   * Get the singleton instance
   */
  public static getInstance(): SyncManager {
    if (!SyncManager.instance) {
      SyncManager.instance = new SyncManager();
    }
    return SyncManager.instance;
  }
  
  /**
   * Handle changes in online status
   */
  private handleOnlineStatusChange(): void {
    const wasOnline = this.isOnline;
    this.isOnline = navigator.onLine;
    
    // Emit connection change event
    this.emit('connectionChange', { online: this.isOnline });
    
    // If we just came back online, process the sync queue
    if (!wasOnline && this.isOnline) {
      console.log('[SyncManager] Device is back online, processing sync queue');
      this.processSyncQueue();
    }
  }
  
  /**
   * Set the sync status and notify listeners
   */
  private setSyncStatus(status: SyncStatus, error?: string): void {
    this.syncStatus = status;
    
    if (status === 'error') {
      this.syncError = error || 'Unknown sync error';
      this.emit('syncError', { error: this.syncError });
    } else {
      this.syncError = null;
    }
    
    if (status === 'success') {
      this.lastSyncTime = Date.now();
    }
    
    this.emit('statusChange', { status });
  }
  
  /**
   * Add an event listener
   */
  public addEventListener(event: string, callback: (data?: any) => void): void {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    
    this.listeners[event].push(callback);
  }
  
  /**
   * Remove an event listener
   */
  public removeEventListener(event: string, callback: (data?: any) => void): void {
    if (!this.listeners[event]) return;
    
    this.listeners[event] = this.listeners[event].filter(listener => listener !== callback);
  }
  
  /**
   * Emit an event to all listeners
   */
  private emit(event: string, data?: any): void {
    if (!this.listeners[event]) return;
    
    this.listeners[event].forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`[SyncManager] Error in ${event} listener:`, error);
      }
    });
  }
  
  /**
   * Check if synchronization is possible
   */
  public canSync(): boolean {
    return this.isOnline && authTokenManager.isAuthenticated();
  }
  
  /**
   * Get the current sync status
   */
  public getSyncStatus(): { status: SyncStatus, lastSyncTime: number, error: string | null } {
    return {
      status: this.syncStatus,
      lastSyncTime: this.lastSyncTime,
      error: this.syncError
    };
  }
  
  /**
   * Add an operation to the sync queue
   */
  public queueSync(operation: () => Promise<void>): void {
    this.syncQueue.push(operation);
    
    // Process the queue if we're online and not already syncing
    if (this.isOnline && !this.isSyncingActive) {
      this.processSyncQueue();
    }
  }
  
  /**
   * Process the sync queue
   */
  private async processSyncQueue(): Promise<void> {
    if (this.syncQueue.length === 0 || this.isSyncingActive || !this.isOnline) {
      return;
    }
    
    this.isSyncingActive = true;
    this.setSyncStatus('syncing');
    
    try {
      while (this.syncQueue.length > 0 && this.isOnline) {
        const operation = this.syncQueue.shift();
        if (operation) {
          await operation();
        }
      }
      
      this.setSyncStatus('success');
    } catch (error) {
      this.setSyncStatus('error', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      this.isSyncingActive = false;
    }
  }
  
  /**
   * Synchronize itineraries with the server
   */
  public async syncItineraries(
    localItineraries: Itinerary[],
    options: SyncOptions = {}
  ): Promise<SyncResult> {
    // Combine with default options
    const syncOptions: Required<SyncOptions> = {
      ...this.defaultOptions,
      ...options
    };
    
    // Check if sync is possible
    if (!this.canSync() && !syncOptions.force) {
      return {
        success: false,
        added: 0,
        updated: 0,
        deleted: 0,
        conflicts: 0,
        error: 'Cannot sync: offline or not authenticated'
      };
    }
    
    this.setSyncStatus('syncing');
    
    try {
      // Initialize counters
      let added = 0;
      let updated = 0;
      let deleted = 0;
      let conflicts = 0;
      
      // Get remote itineraries if needed
      let remoteItineraries: Itinerary[] = [];
      
      if (syncOptions.direction === 'pull' || syncOptions.direction === 'both') {
        const response = await itineraryApiService.getAllItineraries();
        
        if (!response.success) {
          throw new Error(response.error || 'Failed to fetch remote itineraries');
        }
        
        remoteItineraries = response.data || [];
      }
      
      // Create maps for easier lookup
      const localMap = new Map(localItineraries.map(i => [i.id, i]));
      const remoteMap = new Map(remoteItineraries.map(i => [i.id, i]));
      
      // Track changes to apply
      const toCreate: Itinerary[] = [];
      const toUpdate: Itinerary[] = [];
      const toDelete: string[] = [];
      
      // Merged result for local storage
      const mergedItineraries: Itinerary[] = [];
      
      // Process local items for pushing to server
      if (syncOptions.direction === 'push' || syncOptions.direction === 'both') {
        for (const localItem of localItineraries) {
          const remoteItem = remoteMap.get(localItem.id);
          
          if (!remoteItem) {
            // Item exists locally but not remotely - create it
            toCreate.push(localItem);
          } else {
            // Item exists in both places - check timestamps
            const localUpdated = new Date(localItem.updatedAt).getTime();
            const remoteUpdated = new Date(remoteItem.updatedAt).getTime();
            
            if (localUpdated > remoteUpdated) {
              // Local is newer - update remote
              toUpdate.push(localItem);
            }
          }
        }
      }
      
      // Process remote items for pulling to local
      if (syncOptions.direction === 'pull' || syncOptions.direction === 'both') {
        for (const remoteItem of remoteItineraries) {
          const localItem = localMap.get(remoteItem.id);
          
          if (!localItem) {
            // Item exists remotely but not locally - add to local
            mergedItineraries.push(remoteItem);
            added++;
          } else {
            // Item exists in both places - check timestamps
            const localUpdated = new Date(localItem.updatedAt).getTime();
            const remoteUpdated = new Date(remoteItem.updatedAt).getTime();
            
            if (remoteUpdated > localUpdated) {
              // Remote is newer - update local
              mergedItineraries.push(remoteItem);
              updated++;
            } else if (localUpdated > remoteUpdated) {
              // Local is newer - keep local version for now
              mergedItineraries.push(localItem);
            } else {
              // Same timestamp - no conflict
              mergedItineraries.push(localItem);
            }
          }
        }
      }
      
      // Add local-only items to the merged result
      if (syncOptions.direction === 'push' || syncOptions.direction === 'both') {
        for (const localItem of localItineraries) {
          if (!remoteMap.has(localItem.id) && !mergedItineraries.some(i => i.id === localItem.id)) {
            mergedItineraries.push(localItem);
          }
        }
      }
      
      // Apply changes to server
      const apiResults = await Promise.allSettled([
        ...toCreate.map(item => itineraryApiService.createItinerary(item)),
        ...toUpdate.map(item => itineraryApiService.updateItinerary(item.id, item))
      ]);
      
      // Count successes
      for (let i = 0; i < apiResults.length; i++) {
        const result = apiResults[i];
        
        if (result.status === 'fulfilled' && result.value.success) {
          if (i < toCreate.length) {
            added++;
          } else {
            updated++;
          }
        } else if (result.status === 'rejected') {
          conflicts++;
        }
      }
      
      this.setSyncStatus('success');
      
      // Emit sync complete event
      this.emit('syncComplete', {
        added,
        updated,
        deleted,
        conflicts,
        mergedItineraries
      });
      
      return {
        success: true,
        added,
        updated,
        deleted,
        conflicts
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown sync error';
      
      this.setSyncStatus('error', errorMessage);
      
      return {
        success: false,
        added: 0,
        updated: 0,
        deleted: 0,
        conflicts: 0,
        error: errorMessage
      };
    }
  }
  
  /**
   * Resolve conflicts between local and remote data
   */
  private resolveConflict(
    local: Itinerary,
    remote: Itinerary, 
    strategy: ConflictStrategy
  ): Itinerary {
    switch (strategy) {
      case 'local':
        return local;
        
      case 'remote':
        return remote;
        
      case 'merge':
        // For merge strategy, create a new object
        return {
          ...local,
          // Take the newer tripDetails
          tripDetails: this.isNewer(remote.updatedAt, local.updatedAt) 
            ? remote.tripDetails 
            : local.tripDetails,
          // Take the newer budgetSummary
          budgetSummary: this.isNewer(remote.updatedAt, local.updatedAt) 
            ? remote.budgetSummary 
            : local.budgetSummary,
          // Merge days, taking the newer one for each day
          days: this.mergeDays(local.days, remote.days),
          // Use the newest updatedAt
          updatedAt: this.isNewer(remote.updatedAt, local.updatedAt)
            ? remote.updatedAt
            : local.updatedAt
        };
    }
  }
  
  /**
   * Check if timestamp1 is newer than timestamp2
   */
  private isNewer(timestamp1: string, timestamp2: string): boolean {
    return new Date(timestamp1).getTime() > new Date(timestamp2).getTime();
  }
  
  /**
   * Merge days from two itineraries, taking the newer version of each day
   */
  private mergeDays(localDays: any[], remoteDays: any[]): any[] {
    // Create a map of days by dayNumber
    const dayMap = new Map<number, any>();
    
    // Add local days to the map
    localDays.forEach(day => {
      dayMap.set(day.dayNumber, {
        day,
        updatedAt: null // We don't track per-day timestamps yet
      });
    });
    
    // Add remote days, overriding locals if they have a newer timestamp
    remoteDays.forEach(day => {
      const existing = dayMap.get(day.dayNumber);
      
      if (!existing) {
        dayMap.set(day.dayNumber, {
          day,
          updatedAt: null
        });
      }
      // For future: add comparison logic if days have individual timestamps
    });
    
    // Convert map back to array and sort by dayNumber
    return Array.from(dayMap.values())
      .map(entry => entry.day)
      .sort((a, b) => a.dayNumber - b.dayNumber);
  }
}

export const syncManager = SyncManager.getInstance();
